import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import { api, ApiError } from "./lib/api";
import { useResponsive } from "./hooks/useResponsive";
import { Sidebar, MobileHeader } from "./components/layout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ClientsPage } from "./pages/ClientsPage";
import { ClientDetailPage } from "./pages/ClientDetailPage";
import { ServicesPage } from "./pages/ServicesPage";
import { NewAppointmentPage } from "./pages/NewAppointmentPage";
import { AppointmentDetailPage } from "./pages/AppointmentDetailPage";
import { CalendarsPage } from "./pages/CalendarsPage";
import { AvailabilityPage } from "./pages/AvailabilityPage";
import { ResourcesPage } from "./pages/ResourcesPage";
import { LabelsPage } from "./pages/LabelsPage";
import { SavedLocationsPage } from "./pages/SavedLocationsPage";
import { AppointmentAddonsPage } from "./pages/AppointmentAddonsPage";
import { GroupClassesPage } from "./pages/GroupClassesPage";
import { FormsPage } from "./pages/FormsPage";
import { FormDetailPage } from "./pages/FormDetailPage";
import { CalendarPage } from "./pages/CalendarPage";

function AppLayout({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const location = useLocation();
  const { isMobile } = useResponsive();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/") return "Dashboard";
    if (path === "/calendar") return "Calendar";
    if (path.startsWith("/appointments")) return "Calendar";
    if (path.startsWith("/clients")) return "Clients";
    if (path.startsWith("/services")) return "Services";
    if (path.startsWith("/calendars")) return "Calendars";
    if (path.startsWith("/availability")) return "Availability";
    if (path.startsWith("/resources")) return "Resources";
    if (path.startsWith("/labels")) return "Labels";
    if (path.startsWith("/saved-locations")) return "Locations";
    if (path.startsWith("/appointment-addons")) return "Add-ons";
    if (path.startsWith("/classes")) return "Classes";
    if (path.startsWith("/forms")) return "Forms";
    return "Wooflit";
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar
        isMobile={isMobile}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {isMobile && (
          <MobileHeader
            title={getPageTitle()}
            onMenuClick={() => setSidebarOpen(true)}
          />
        )}
        
        {!isMobile && (
          <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-end">
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">{user?.name}</span>
              <button 
                onClick={logout} 
                className="text-sm text-slate-600 hover:text-slate-900"
              >
                Sign out
              </button>
            </div>
          </header>
        )}

        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}

function AuthenticatedRoutes() {
  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:clientId" element={<ClientDetailPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/calendars" element={<CalendarsPage />} />
        <Route path="/availability" element={<AvailabilityPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/labels" element={<LabelsPage />} />
        <Route path="/saved-locations" element={<SavedLocationsPage />} />
        <Route path="/appointment-addons" element={<AppointmentAddonsPage />} />
        <Route path="/classes" element={<GroupClassesPage />} />
        <Route path="/forms" element={<FormsPage />} />
        <Route path="/forms/:formId" element={<FormDetailPage />} />
        {/* Appointments: redirect list to calendar, but keep new and detail routes */}
        <Route path="/appointments" element={<Navigate to="/calendar" replace />} />
        <Route path="/appointments/new" element={<NewAppointmentPage />} />
        <Route path="/appointments/:appointmentId" element={<AppointmentDetailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

function AppContent() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const setUser = useAuthStore((state) => state.setUser);
  const setLoading = useAuthStore((state) => state.setLoading);
  const logout = useAuthStore((state) => state.logout);
  const initialize = useAuthStore((state) => state.initialize);

  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (window.location.pathname === "/reset-password" && token) {
      setResetToken(token);
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!isAuthenticated || !isLoading) return;

    const fetchUser = async () => {
      try {
        const user = await api.me.get();
        setUser(user);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          logout();
        }
        console.error("Failed to fetch user:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [isAuthenticated, isLoading, setUser, setLoading, logout]);

  const handleResetSuccess = () => {
    setResetSuccess(true);
    window.history.replaceState({}, document.title, "/");
    timeoutRef.current = setTimeout(() => {
      setResetToken(null);
      setResetSuccess(false);
    }, 2000);
  };

  if (resetToken && !resetSuccess) {
    return <ResetPasswordPage token={resetToken} onSuccess={handleResetSuccess} />;
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <h1 className="text-3xl font-bold text-slate-900">Wooflit</h1>
          <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-700">Password reset successful! Redirecting to login...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Wooflit</h1>
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AuthenticatedRoutes />;
}

export function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
